import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import {
  map,
  catchError,
  switchMap,
  withLatestFrom,
  tap,
  mergeMap,
  concatMap,
} from 'rxjs/operators';
import { of } from 'rxjs';
import { getErrorType } from 'src/app/shared/utils/error.utils';
import {
  ApiKeyCreatedAction,
  ApiKeyPageLoadedAction,
  ApiKeyPageRequestedAction,
  ApiKeysErroredAction,
  ApiKeysLoadedAction,
  NewApiKeySubmittedAction,
  ApiKeyRevokeRequestedAction,
  ApiKeyRevokeRequestedFailedAction,
  ApiKeyRevokeRequestedSuccessAction,
  UpdateApiKeySubmittedAction,
  ApiKeyUpdatedAction,
} from './api-key.actions';
import { ApiKeyService } from './api-key.service';
import { ApiKeyApiError, ApiKeyApiErrorTypes } from './api-key.errors';
import { Store } from '@ngrx/store';
import { AppStore } from '../store.model';
import { getApiKeysSort } from './api-key.selectors';
import { Router } from '@angular/router';
import { FormMode } from '../api-key-form/api-key-form.model';

@Injectable()
export class ApiKeyEffects {
  loadApiKeys$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ApiKeyPageLoadedAction,
        ApiKeyPageRequestedAction,
        ApiKeyCreatedAction,
        ApiKeyUpdatedAction,
        ApiKeyRevokeRequestedSuccessAction,
      ),
      withLatestFrom(this.store.select(getApiKeysSort)),
      switchMap(([, sort]) =>
        this.apiKeyService.loadApiKeys(sort).pipe(
          map((apiKeys) => ApiKeysLoadedAction({ apiKeys })),
          catchError((err) =>
            of(
              ApiKeysErroredAction({
                error: new ApiKeyApiError(getErrorType(ApiKeyApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  createApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NewApiKeySubmittedAction),
      switchMap((action) =>
        this.apiKeyService.createApiKey(action.apiKey).pipe(
          // Navigate to the edit page with our generated api key
          tap((data) =>
            this.router.navigate(['/api-keys', data.id], {
              state: { previousFormMode: FormMode.Create, generatedApiKey: data.apiKey },
            }),
          ),
          // Refetch all keys so we maintain order
          map(() => ApiKeyCreatedAction()),
          catchError((err) =>
            of(
              ApiKeysErroredAction({
                error: new ApiKeyApiError(getErrorType(ApiKeyApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateApiKeySubmittedAction),
      concatMap(({ apiKeyId, apiKey: key }) =>
        this.apiKeyService.updateApiKey(apiKeyId, key).pipe(
          map((apiKey) => ApiKeyUpdatedAction({ apiKey })),
          catchError((err) =>
            of(
              ApiKeysErroredAction({
                error: new ApiKeyApiError(getErrorType(ApiKeyApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  redirectAfterUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ApiKeyUpdatedAction),
        tap(({ apiKey }) =>
          this.router.navigate(['/api-keys', apiKey.id], {
            state: { previousFormMode: FormMode.Edit, generatedApiKey: apiKey.apiKey },
          }),
        ),
      ),
    {
      dispatch: false,
    },
  );

  revokeApiKey$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ApiKeyRevokeRequestedAction),
      mergeMap(({ apiKeyId }) =>
        this.apiKeyService.revokeApiKey(apiKeyId).pipe(
          map(() => ApiKeyRevokeRequestedSuccessAction()),
          catchError((err) =>
            of(
              ApiKeyRevokeRequestedFailedAction({
                error: new ApiKeyApiError(getErrorType(ApiKeyApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private apiKeyService: ApiKeyService,
    private store: Store<AppStore>,
    private router: Router,
  ) {}
}
